"use client";

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronDownIcon } from "lucide-react";

import { cx } from "../../cx";
import styles from "./accordion.module.css";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

function Accordion({
  className,
  ...props
}: WithClassName<AccordionPrimitive.Root.Props>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={className}
      {...props}
    />
  );
}

function AccordionItem({
  className,
  ...props
}: WithClassName<AccordionPrimitive.Item.Props>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cx(styles.item, className)}
      {...props}
    />
  );
}

/** The trigger sits in Base UI's Header — an `<h3>` unless `headingLevel`
 *  says otherwise — and ends in a chevron that turns while its panel is
 *  open. */
function AccordionTrigger({
  className,
  children,
  headingLevel = 3,
  ...props
}: WithClassName<AccordionPrimitive.Trigger.Props> & {
  headingLevel?: 2 | 3 | 4 | 5 | 6;
}) {
  const Heading = `h${headingLevel}` as const;
  return (
    <AccordionPrimitive.Header
      data-slot="accordion-header"
      render={<Heading />}
      className={styles.header}
    >
      <AccordionPrimitive.Trigger
        data-tui=""
        data-slot="accordion-trigger"
        className={cx(styles.trigger, className)}
        {...props}
      >
        {children}
        <ChevronDownIcon
          aria-hidden
          data-slot="accordion-chevron"
          className={styles.chevron}
          strokeWidth={2}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

/** Closed panels stay in the DOM as `hidden="until-found"`, so their text is
 *  in the server HTML and the browser's find-in-page opens the panel it
 *  matches. */
function AccordionContent({
  className,
  children,
  ...props
}: WithClassName<AccordionPrimitive.Panel.Props>) {
  return (
    <AccordionPrimitive.Panel
      data-tui=""
      data-slot="accordion-content"
      hiddenUntilFound
      className={styles.content}
      {...props}
    >
      <div className={cx(styles.inner, className)}>{children}</div>
    </AccordionPrimitive.Panel>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
